import { DeleteOutlined, EditOutlined, EyeOutlined, PlusOutlined, SearchOutlined } from "@ant-design/icons";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { App, Button, Col, Drawer, Form, Input, InputNumber, Popconfirm, Row, Select, Space, Table, Tag, Typography } from "antd";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import apiClient from "@/app/api/client";
import { usePagination } from "@/shared/hooks/usePagination";
import { weddingHallService } from "../services/weddingHall.service";

const { Title } = Typography;

export function WeddingHallsPage() {
	const navigate = useNavigate();
	const { message } = App.useApp();
	const queryClient = useQueryClient();
	const { page, pageSize, onChange } = usePagination();
	const [search, setSearch] = useState("");
	const [drawerOpen, setDrawerOpen] = useState(false);
	const [editingId, setEditingId] = useState<number | null>(null);
	const [form] = Form.useForm();

	// ==================== Data ====================
	const { data, isLoading } = useQuery({
		queryKey: ["wedding-halls", { page, per_page: pageSize, search }],
		queryFn: () => weddingHallService.list({ page, per_page: pageSize, search: search || undefined }),
	});

	const { data: regionsData } = useQuery({
		queryKey: ["regions"],
		queryFn: () => apiClient.get("/regions", { params: { per_page: 100 } }).then((r) => r.data),
	});

	const regionId = Form.useWatch("region_id", form);
	const { data: districtsData } = useQuery({
		queryKey: ["districts", regionId],
		queryFn: () => apiClient.get("/districts", { params: { region_id: regionId, per_page: 100 } }).then((r) => r.data),
		enabled: !!regionId,
	});

	const districtId = Form.useWatch("district_id", form);
	const { data: mahallasData } = useQuery({
		queryKey: ["mahallas", districtId],
		queryFn: () => apiClient.get("/mahallas", { params: { district_id: districtId, per_page: 100 } }).then((r) => r.data),
		enabled: !!districtId,
	});

	// ==================== Mutations ====================
	const saveMutation = useMutation({
		mutationFn: (values: Record<string, unknown>) =>
			editingId ? weddingHallService.update(editingId, values) : weddingHallService.create(values),
		onSuccess: () => {
			message.success(editingId ? "Yangilandi" : "Yaratildi");
			closeDrawer();
			queryClient.invalidateQueries({ queryKey: ["wedding-halls"] });
		},
		onError: () => message.error("Xatolik yuz berdi"),
	});

	const deleteMutation = useMutation({
		mutationFn: (id: number) => weddingHallService.remove(id),
		onSuccess: () => {
			message.success("O'chirildi");
			queryClient.invalidateQueries({ queryKey: ["wedding-halls"] });
		},
	});

	// ==================== Drawer ====================
	const openCreate = () => {
		setEditingId(null);
		form.resetFields();
		setDrawerOpen(true);
	};

	const openEdit = (record: Record<string, unknown>) => {
		setEditingId(record.id as number);
		form.setFieldsValue(record);
		setDrawerOpen(true);
	};

	const closeDrawer = () => {
		setDrawerOpen(false);
		setEditingId(null);
		form.resetFields();
	};

	// ==================== Table ====================
	const halls = data?.data?.wedding_halls || [];
	const pagination = data?.data?.pagination;

	const workStatusColor: Record<string, string> = {
		working: "green", repair: "orange", closed: "red", temporarily_closed: "volcano",
	};

	const columns = [
		{ title: "ID", dataIndex: "id", width: 60 },
		{ title: "Nomi", dataIndex: "name" },
		{ title: "INN", dataIndex: "inn" },
		{ title: "Telefon", dataIndex: "phone" },
		{
			title: "Viloyat", key: "region",
			render: (_: unknown, r: Record<string, unknown>) => (r.region as { title: string } | null)?.title || "—",
		},
		{ title: "Sig'imi", dataIndex: "capacity", render: (v: number) => v || "—" },
		{
			title: "Holat", dataIndex: "work_status",
			render: (v: string) => <Tag color={workStatusColor[v] || "default"}>{v}</Tag>,
		},
		{
			title: "Amallar", width: 150,
			render: (_: unknown, record: Record<string, unknown>) => (
				<Space>
					<Button size="small" icon={<EyeOutlined />} onClick={() => navigate(`/wedding-halls/${record.id}`)} />
					<Button size="small" icon={<EditOutlined />} onClick={() => openEdit(record)} />
					<Popconfirm title="O'chirishni tasdiqlaysizmi?" onConfirm={() => deleteMutation.mutate(record.id as number)}>
						<Button size="small" danger icon={<DeleteOutlined />} />
					</Popconfirm>
				</Space>
			),
		},
	];

	return (
		<div>
			<div className="flex justify-between items-center mb-4">
				<Title level={3} className="!mb-0">To'yxonalar</Title>
				<Button type="primary" icon={<PlusOutlined />} onClick={openCreate}>Qo'shish</Button>
			</div>
			<Input placeholder="Qidiruv..." prefix={<SearchOutlined />} className="mb-4 max-w-xs"
				value={search} onChange={(e) => setSearch(e.target.value)} allowClear />
			<Table columns={columns} dataSource={halls} rowKey="id" loading={isLoading}
				pagination={{
					current: pagination?.current_page || 1, pageSize: pagination?.per_page || 20,
					total: pagination?.total_items || 0, onChange, showSizeChanger: true,
					showTotal: (total) => `Jami: ${total}`,
				}} />

			{/* ==================== Drawer ==================== */}
			<Drawer
				title={editingId ? "To'yxonani tahrirlash" : "Yangi to'yxona"}
				open={drawerOpen}
				onClose={closeDrawer}
				width={520}
				extra={
					<Space>
						<Button onClick={closeDrawer}>Bekor qilish</Button>
						<Button type="primary" onClick={() => form.submit()} loading={saveMutation.isPending}>Saqlash</Button>
					</Space>
				}
			>
				<Form form={form} layout="vertical" onFinish={(v) => saveMutation.mutate(v)}>
					<Form.Item name="name" label="Nomi" rules={[{ required: true }]}>
						<Input />
					</Form.Item>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item name="inn" label="INN" rules={[{ required: true }]}>
								<InputNumber className="w-full" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="capacity" label="Sig'imi (odam)">
								<InputNumber className="w-full" />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item name="phone" label="Telefon">
								<Input />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="phone2" label="Telefon 2">
								<Input />
							</Form.Item>
						</Col>
					</Row>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item name="region_id" label="Viloyat">
								<Select placeholder="Tanlang" allowClear
									options={regionsData?.data?.regions?.map((r: { id: number; title: string }) => ({ value: r.id, label: r.title }))}
									onChange={() => { form.setFieldValue("district_id", undefined); form.setFieldValue("mahalla_id", undefined); }} />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="district_id" label="Tuman">
								<Select placeholder="Avval viloyat tanlang" allowClear disabled={!regionId}
									options={districtsData?.data?.districts?.map((d: { id: number; title: string }) => ({ value: d.id, label: d.title }))}
									onChange={() => form.setFieldValue("mahalla_id", undefined)} />
							</Form.Item>
						</Col>
					</Row>
					<Form.Item name="mahalla_id" label="Mahalla">
						<Select placeholder="Avval tuman tanlang" allowClear showSearch optionFilterProp="label" disabled={!districtId}
							options={mahallasData?.data?.mahallas?.map((m: { id: number; title: string }) => ({ value: m.id, label: m.title }))} />
					</Form.Item>
					<Form.Item name="address" label="Manzil">
						<Input />
					</Form.Item>
					<Row gutter={16}>
						<Col span={12}>
							<Form.Item name="price" label="Narxi">
								<InputNumber className="w-full" />
							</Form.Item>
						</Col>
						<Col span={12}>
							<Form.Item name="price_type" label="Narx turi" initialValue="per_hall">
								<Select options={[{ value: "per_hall", label: "Butun zal" }, { value: "per_seat", label: "Har bir o'rindiq" }]} />
							</Form.Item>
						</Col>
					</Row>
					<Form.Item name="work_status" label="Ish holati" initialValue="working">
						<Select options={[
							{ value: "working", label: "Ishlayapti" },
							{ value: "repair", label: "Ta'mirda" },
							{ value: "closed", label: "Yopiq" },
							{ value: "temporarily_closed", label: "Vaqtincha yopiq" },
						]} />
					</Form.Item>
					<Form.Item name="description" label="Tavsif">
						<Input.TextArea rows={3} />
					</Form.Item>
				</Form>
			</Drawer>
		</div>
	);
}
